import React, { useState, useEffect, useMemo, useRef } from 'react';
import { policyDocumentApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { useAuth } from '@/lib/auth';
import { canManagePolicies, canDeletePolicies } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Plus, Upload, Edit, Trash2, FileText, Image, Video,
  FileSpreadsheet, File, Loader2, Download, Eye,
} from 'lucide-react';

const POLICY_TYPES = [
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'excel', label: 'Excel' },
  { value: 'word', label: 'Word' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'txt', label: 'Text' },
  { value: 'other', label: 'Other' },
];

const typeIcons = {
  pdf: FileText,
  image: Image,
  video: Video,
  excel: FileSpreadsheet,
  word: FileText,
  presentation: FileText,
  txt: FileText,
  other: File,
};

const typeColor = (type) => {
  const colors = {
    pdf: 'bg-red-100 text-red-700 border-red-200',
    image: 'bg-purple-100 text-purple-700 border-purple-200',
    video: 'bg-pink-100 text-pink-700 border-pink-200',
    excel: 'bg-green-100 text-green-700 border-green-200',
    word: 'bg-blue-100 text-blue-700 border-blue-200',
    presentation: 'bg-orange-100 text-orange-700 border-orange-200',
    txt: 'bg-gray-100 text-gray-700 border-gray-200',
    other: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return colors[type] || colors.other;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getDocId = (doc) => doc?._id || doc?.id;

const stripExtension = (filename) => {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
};

const normalizeListResponse = (value) => {
  if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pages: 1 };
  if (Array.isArray(value?.items)) {
    return {
      items: value.items,
      total: value?.total ?? value.items.length,
      page: value?.page ?? 1,
      pages: value?.pages ?? 1,
    };
  }
  return { items: [], total: 0, page: 1, pages: 1 };
};

export default function PolicyPage() {
  const { user } = useAuth();
  const canManage = canManagePolicies(user?.role);
  const canDelete = canDeletePolicies(user?.role);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);

  const [uploadData, setUploadData] = useState(null);
  const [uploadForm, setUploadForm] = useState({ name: '', description: '', type: 'pdf' });
  const [editForm, setEditForm] = useState({ name: '', description: '', type: 'pdf' });

  const fileInputRef = useRef(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const queryFilters = useMemo(() => {
    const params = { page, limit: pageSize };
    const trimmedSearch = debouncedSearch?.trim?.();
    if (trimmedSearch) params.search = trimmedSearch;
    if (typeFilter !== 'all') params.type = typeFilter;
    return params;
  }, [page, pageSize, debouncedSearch, typeFilter]);

  const fetchDocuments = async () => {
    try {
      const response = normalizeListResponse(await policyDocumentApi.getAll(queryFilters));
      setDocuments(response.items);
      setTotalDocs(response.total);
      setTotalPages(response.pages);
      setPage((p) => clampPageToTotal(p, response.pages));
    } catch (err) {
      console.error('Failed to fetch policy documents:', err);
      toast.error(err.message || 'Failed to load policy documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [queryFilters]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter]);

  const resetUploadState = () => {
    setUploadData(null);
    setUploadForm({ name: '', description: '', type: 'pdf' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await policyDocumentApi.upload(file);
      setUploadData(result);
      setUploadForm({
        name: stripExtension(result?.originalName) || file.name,
        description: '',
        type: result?.suggestedType || 'other',
      });
      toast.success('File uploaded — add details and save');
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
      resetUploadState();
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!uploadData?.s3Url || !uploadData?.s3Key) {
      toast.error('Please upload a file first');
      return;
    }
    if (!uploadForm.name?.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setSaving(true);
    try {
      const created = await policyDocumentApi.create({
        name: uploadForm.name.trim(),
        description: uploadForm.description?.trim() || '',
        s3Url: uploadData.s3Url,
        s3Key: uploadData.s3Key,
        type: uploadForm.type,
        fileSize: uploadData.fileSize,
      });
      setDocuments((prev) => [created, ...prev]);
      setTotalDocs((prev) => prev + 1);
      toast.success('Policy document saved');
      setUploadOpen(false);
      resetUploadState();
      fetchDocuments();
    } catch (err) {
      toast.error(err.message || 'Failed to save policy document');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (doc) => {
    setSelectedDoc(doc);
    setEditForm({
      name: doc?.name || '',
      description: doc?.description || '',
      type: doc?.type || 'other',
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    const docId = getDocId(selectedDoc);
    if (!docId) return;
    if (!editForm.name?.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setSaving(true);
    try {
      const updated = await policyDocumentApi.update(docId, {
        name: editForm.name.trim(),
        description: editForm.description?.trim() || '',
        type: editForm.type,
      });
      setDocuments((prev) =>
        prev.map((d) => (getDocId(d) === docId ? { ...d, ...updated } : d))
      );
      toast.success('Policy document updated');
      setEditOpen(false);
      setSelectedDoc(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update policy document');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const docId = getDocId(selectedDoc);
    if (!docId) return;

    setSaving(true);
    try {
      await policyDocumentApi.remove(docId);
      toast.success('Policy document deleted');
      setDeleteOpen(false);
      setSelectedDoc(null);
      fetchDocuments();
    } catch (err) {
      toast.error(err.message || 'Failed to delete policy document');
    } finally {
      setSaving(false);
    }
  };

  const handleView = async (doc) => {
    const docId = getDocId(doc);
    if (!docId) return;

    setViewingId(docId);
    try {
      const result = await policyDocumentApi.getSignedUrl(docId);
      const url = typeof result === 'string' ? result : result?.url || result?.signedUrl;
      if (!url) {
        toast.error('Unable to open document');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.message || 'Failed to open document');
    } finally {
      setViewingId(null);
    }
  };

  const handleDownload = async (doc) => {
    const docId = getDocId(doc);
    if (!docId) return;

    setDownloadingId(docId);
    try {
      const response = await policyDocumentApi.download(docId);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName = matchedFileName || doc?.name || 'policy-document';

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (err) {
      toast.error(err?.message || 'Failed to download document');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="policy-page" className="space-y-3 animate-fadeIn">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Policy Documents</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            View and manage company policy documents
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => {
              resetUploadState();
              setUploadOpen(true);
            }}
            data-testid="upload-policy-btn"
            className="w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload Policy
          </Button>
        )}
      </div>

      <Card className="border border-border shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="policy-search"
              placeholder="Search by name or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="policy-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {POLICY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border border-border shadow-sm overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-widest font-medium">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium hidden md:table-cell">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium hidden sm:table-cell">Size</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium hidden lg:table-cell">Updated</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => {
                const docId = getDocId(doc);
                const Icon = typeIcons[doc?.type] || File;
                const isDownloading = downloadingId === docId;
                const isViewing = viewingId === docId;

                return (
                  <TableRow key={docId} data-testid={`policy-row-${docId}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-md bg-[#0047AB]/10 flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-[#0047AB]" />
                        </div>
                        <span className="text-sm font-medium truncate">{doc?.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate hidden md:table-cell">
                      {doc?.description || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge className={typeColor(doc?.type)}>
                        {doc?.type || 'other'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                      {formatFileSize(doc?.fileSize)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden lg:table-cell">
                      {doc?.updatedAt
                        ? new Date(doc.updatedAt).toLocaleDateString()
                        : doc?.createdAt
                          ? new Date(doc.createdAt).toLocaleDateString()
                          : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => handleView(doc)}
                          disabled={isViewing}
                          aria-label={`View ${doc?.name}`}
                          data-testid={`policy-view-${docId}`}
                        >
                          {isViewing
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => handleDownload(doc)}
                          disabled={isDownloading}
                          aria-label={`Download ${doc?.name}`}
                          data-testid={`policy-download-${docId}`}
                        >
                          {isDownloading
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <Download className="w-4 h-4" />}
                        </Button>
                        {canManage && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => handleOpenEdit(doc)}
                            aria-label={`Edit ${doc?.name}`}
                            data-testid={`policy-edit-${docId}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setDeleteOpen(true);
                            }}
                            aria-label={`Delete ${doc?.name}`}
                            data-testid={`policy-delete-${docId}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {totalDocs === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No policy documents found</p>
                    {canManage && (
                      <p className="text-xs mt-1">Upload a policy document to get started</p>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalDocs} documents)
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[70px] h-8 text-sm" data-testid="policy-page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1}
            data-testid="policy-prev-page-btn"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page >= totalPages}
            data-testid="policy-next-page-btn"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          setUploadOpen(open);
          if (!open) resetUploadState();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Policy Document</DialogTitle>
            <DialogDescription>
              Upload a file, then add the document name and description.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>File</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.xlsx,.xls,.csv,.doc,.docx,.ppt,.pptx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="policy-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadData ? 'Change File' : 'Choose File'}
                    </>
                  )}
                </Button>
              </div>
              {uploadData?.originalName && (
                <p className="text-xs text-muted-foreground">
                  {uploadData.originalName} ({formatFileSize(uploadData.fileSize)})
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-name">Name *</Label>
              <Input
                id="upload-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Employee Handbook"
                disabled={!uploadData}
                data-testid="policy-upload-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="upload-description">Description</Label>
              <Textarea
                id="upload-description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this policy document"
                rows={3}
                disabled={!uploadData}
                data-testid="policy-upload-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={uploadForm.type}
                onValueChange={(v) => setUploadForm((prev) => ({ ...prev, type: v }))}
                disabled={!uploadData}
              >
                <SelectTrigger data-testid="policy-upload-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POLICY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={!uploadData || saving}
              data-testid="policy-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Policy Document</DialogTitle>
            <DialogDescription>Update the document name, description, or type.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                data-testid="policy-edit-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="policy-edit-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={editForm.type}
                onValueChange={(v) => setEditForm((prev) => ({ ...prev, type: v }))}
              >
                <SelectTrigger data-testid="policy-edit-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POLICY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="policy-update-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Policy Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{selectedDoc?.name}&rdquo;? This will permanently remove the file from storage.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
              data-testid="policy-confirm-delete-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
